import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Button, ButtonGroup, Pagination, Row, Col } from 'react-bootstrap';

/**
 * Given FlyerAlarm API response, display available variants and quantities.
 */
const FlyerAlarmVariantsList = ({ productGroupData, lookupOptions, children }) => {
  if (!productGroupData) return null;

  const {
    messages: { perforation: text },
  } = useIntl();

  const {
    name = '',
    // description = '',
    // description_html: descriptionHtml = '',
    product_group_id: productGroupId,
    product_group_image: productGroupImage = '',
    product_variants: productVariants = [],
    // has_quantities: hasQuantities = false,
  } = productGroupData;

  if (!name) return null;

  const [selectedVariantId, setSelectedVariantId] = useState();
  const [quantityPage, setQuantityPage] = useState(1);

  useEffect(() => {
    setQuantityPage(1);
    setSelectedVariantId(null);
  }, [productGroupId]);

  const mrCSS = {
    marginRight: '0.5em',
  };

  // Variants pagination, as some product groups (i.g. "7050") has 2000+ quantities
  const productVariantsWithQuantities = productVariants.filter(({ has_quantities: hasQuantities }) => hasQuantities);
  const totalVariants = productVariantsWithQuantities.length;
  const perPage = 50;
  const totalPages = Math.ceil(totalVariants / perPage);
  const pageItems = [];
  const makeItem = i => (
    <Pagination.Item onClick={() => setQuantityPage(i)} key={i} active={quantityPage === i}>
      {i}
    </Pagination.Item>
  );

  if (totalPages > 9) {
    pageItems.push(makeItem(1));
    if (quantityPage >= 5) pageItems.push(<Pagination.Ellipsis key="ellipsisLeft" />);
    const leftBoundary = Math.max(2, Math.min(totalPages - 5 + 1, quantityPage - 2));
    const rightBoundary = Math.min(totalPages - 1, Math.max(5, quantityPage + 2));
    for (let i = leftBoundary; i <= rightBoundary; i++) {
      pageItems.push(makeItem(i));
    }
    if (quantityPage <= totalPages - 5 + 1) pageItems.push(<Pagination.Ellipsis key="ellipsisRight" />);
    pageItems.push(makeItem(totalPages));
  } else {
    pageItems.push(...Array.from({ length: totalPages }, (_, i) => makeItem(i + 1)));
  }

  const variantsToSelect = productVariantsWithQuantities
    .slice((quantityPage - 1) * perPage, quantityPage * perPage)
    .map(({ product_variant_id: productVariantId, quantities }) => (
      <Button
        style={mrCSS}
        bsSize="xsmall"
        key={productVariantId}
        onClick={() => setSelectedVariantId(productVariantId)}
      >
        #{productVariantId} ({quantities.length})
      </Button>
    ));

  const handleQuantityShippingClick = (quantityId, shippingType) => {
    // TODO: waiting state
    lookupOptions(quantityId, shippingType);
  };

  const quantitiesToSelect = variantId => {
    const productVariant = productVariants.find(
      ({ product_variant_id: productVariantId }) => productVariantId === variantId
    );
    if (!productVariant) {
      return [];
    }

    const { quantities } = productVariant;
    return quantities.map(
      ({
        quantity_id: quantityId,
        quantity,
        Standard_available: hasStandard,
        Express_available: hasExpress,
        Overnight_available: hasOvernight,
        Sameday_available: hasSameday,
      }) => {
        const shippingTypes = {
          standard: hasStandard,
          express: hasExpress,
          overnight: hasOvernight,
          sameday: hasSameday,
        };

        const shipButtons = Object.entries(shippingTypes)
          .filter(([, enabled]) => enabled)
          .map(([shippingType]) => (
            <Button
              key={[quantityId, shippingType].join(':')}
              onClick={() => {
                handleQuantityShippingClick(quantityId, shippingType);
              }}
              style={mrCSS}
              bsSize="xsmall"
            >
              {shippingType}
            </Button>
          ));

        return (
          <div key={quantityId}>
            {quantityId} ({quantity}) <ButtonGroup bsSize="xsmall">{shipButtons}</ButtonGroup>
          </div>
        );
      }
    );
  };

  const selectedQuantities = quantitiesToSelect(selectedVariantId);

  const limitedHeightCSS = {
    height: '9rem',
    overflow: 'auto',
    border: '1px solid #DDD',
    padding: '0.5em',
  };

  return (
    <>
      <Row>
        <Col xs={12} md={4}>
          <Row>
            <Col xs={12} md={9}>
              <h6>
                {text.product_group} #{productGroupId}
              </h6>
              <h5>{name}</h5>
              {/* <p style={limitedHeightCSS}>{description}</p> */}
            </Col>
            <Col xs={12} md={3}>
              <img src={productGroupImage} alt={name} style={{ width: '100%', maxWidth: '100px' }} />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={4}>
          <h4>
            {totalVariants} {text.variants}
          </h4>
          <p>{text.select_variant}</p>
          <div style={{ marginTop: '1em', ...limitedHeightCSS }}>{variantsToSelect}</div>
          {totalPages > 1 && (
            <Pagination style={{ margin: '0' }} bsSize="small">
              {pageItems}
            </Pagination>
          )}
        </Col>
        <Col xs={12} md={4}>
          {!!selectedVariantId && (
            <>
              <h4>
                {selectedQuantities.length} {text.quantities_in_variant} &laquo;{selectedVariantId}&raquo;
              </h4>
              <p>{text.select_shipping_type}</p>
              <div style={{ marginTop: '1em', ...limitedHeightCSS }}>{selectedQuantities}</div>
            </>
          )}
        </Col>
        <Col xs={12} md={4}>
          {children}
        </Col>
      </Row>
    </>
  );
};

export default FlyerAlarmVariantsList;
